import React from 'react';
import { SimpleReportLayout } from './SimpleReportLayout';
import { ReportControls } from '../tables/ReportControls';
import { SpringPagination } from '../tables/SpringPagination';
import { DataTable } from '../tables/DataTable';
import { ReportMobileSummary } from '../tables/ReportMobileSummary';
import { ReportErrorState, ReportStaleBar, ReportPendingState } from '../tables/ReportErrorState';
import type { ReportSummaryStat } from '../tables/ReportMobileSummary';
import type { Column } from '../tables/DataTable';
import type { useSpringReport } from '../../hooks/use-spring-report';
import type { useReportDataAdapters } from '../../hooks/use-report-data-adapters';

/**
 * Props for ReportPageLayout component
 *
 * @template TDto - The DTO type for the report data
 */
export interface ReportPageLayoutProps<TDto> {
    /**
     * Titlul paginii, randat ca `<h1>` in `SimpleReportLayout`. Fiecare
     * consumator isi da titlul propriu — nu exista un implicit derivat din
     * rута, pentru ca aceeasi pagina poate fi inregistrata pe mai multe rute
     * cu etichete de meniu diferite (ex. `SchoolsReportPage`).
     */
    title?: string;

    /**
     * Report interface from useSpringReport or useReportPage
     */
    report: ReturnType<typeof useSpringReport<TDto, any>>;

    /**
     * Data adapters from useReportDataAdapters or useReportPage
     */
    adapters: ReturnType<typeof useReportDataAdapters>;

    /**
     * Excel export handler from useReportExport or useReportPage
     */
    handleExport: () => Promise<void>;

    /**
     * Column definitions for the DataTable
     */
    columns: Column<TDto>[];

    /**
     * Optional extra controls to render before report details
     * (e.g., EditionSelector, role filter, etc.)
     *
     * Singurele controale care raman vizibile SI pe mobil: sunt filtre de
     * context (editia), nu unelte de tabel. Se randeaza o singura data, in bara
     * de controale — nu duplicat in rezumat, ca sa nu existe doua elemente cu
     * acelasi `id` in DOM.
     */
    extraControls?: React.ReactNode;

    /**
     * Whether to show the export button
     * @default true
     */
    showExport?: boolean;

    /**
     * Numele entitatii la plural, pentru totalul din rezumatul de pe mobil:
     * „scoli", „utilizatori", „comenzi". Implicit: „rezultate".
     */
    entityLabel?: string;

    /**
     * Cifrele afisate in capul rezumatului de pe mobil. Fara ele se afiseaza
     * doar totalul de randuri, care se stie mereu din paginare.
     */
    mobileStats?: ReportSummaryStat[];

    /**
     * Unitatea cifrei din dreapta fiecarui rand in rezumatul de pe mobil
     * („kg", „elevi"). E o constanta a raportului, nu o valoare din rand.
     */
    unitLabel?: string;

    /**
     * Textul starii de gol, identic pe desktop (`DataTable`) si mobil
     * (`ReportMobileSummary`) — o singura sursa, ca sa nu diverga din nou intre
     * cele doua randari. Implicit `DEFAULT_EMPTY_MESSAGE`.
     */
    emptyMessage?: string;
}

/**
 * Standardized layout component for all report pages
 *
 * Eliminates 60+ lines of duplicated JSX across report pages by consolidating:
 * - SimpleReportLayout structure (3-section layout with fixed controls/pagination)
 * - ReportControls (top bar with report details, reset, refresh, export)
 * - SpringPagination (bottom bar with page navigation and size selector)
 * - DataTable (scrollable middle section with data display)
 *
 * ## 🔴 Doua randari diferite, nu una responsiva
 *
 * **De la `md` (768px) in sus:** tabelul complet — toate coloanele, filtrele pe
 * coloana, sortarea, paginarea si exportul Excel. Nu se ascunde nicio coloana
 * (decizia owner-ului, 2026-09-03: *„te rog lasă-le pe toate vizibile"*).
 *
 * **Sub `md`:** `ReportMobileSummary` — cifre, primele trei randuri si trimiterea
 * la desktop. **Zero tabel, zero export.** Nu e o degradare a tabelului, e alt
 * ecran: un tabel de 51 de coloane nu devine utilizabil pe 360px prin
 * rearanjare. Randarea e conditionata din CSS (`hidden md:block`), deci nu
 * depinde de un `matchMedia` care se schimba dupa montare.
 *
 * ## Usage
 *
 * Simple report page:
 * ```tsx
 * function SchoolsReportPage() {
 *     const { report, adapters, handleExport } = useReportPage({
 *         queryHook: api.schools.report,
 *         initialSort: ['numeInstitutieInvatamant,asc'],
 *         exportEndpoint: '/api/schools/report',
 *         exportFilename: 'raport_scoli'
 *     });
 *
 *     const columns = useMemo(() =>
 *         columnBuilder<SchoolReportDto>()
 *             // `cardRole` merge prin `options`, fara sa atinga column-builder.ts
 *             .text('numeInstitutieInvatamant', 'Nume', 'Denumire...', { cardRole: 'title' })
 *             .text('cui', 'CUI', 'C.U.I....')
 *             .build(),
 *         []
 *     );
 *
 *     return (
 *         <ReportPageLayout
 *             report={report}
 *             adapters={adapters}
 *             handleExport={handleExport}
 *             columns={columns}
 *             entityLabel="scoli"
 *         />
 *     );
 * }
 * ```
 *
 * Complex report page with extra controls:
 * ```tsx
 * function WasteOrdersReportPage() {
 *     const { report, adapters, handleExport } = useReportPage({...});
 *     const columns = useMemo(() => columnBuilder()...);
 *
 *     return (
 *         <ReportPageLayout
 *             report={report}
 *             adapters={adapters}
 *             handleExport={handleExport}
 *             columns={columns}
 *             extraControls={
 *                 <EditionSelector
 *                     editions={editions}
 *                     currentEditionId={editionId}
 *                     onChange={setEditionId}
 *                 />
 *             }
 *         />
 *     );
 * }
 * ```
 *
 * @template TDto - The DTO type for the report data
 */
export function ReportPageLayout<TDto extends Record<string, any>>({
    title,
    report,
    adapters,
    handleExport,
    columns,
    extraControls,
    showExport = true,
    entityLabel,
    mobileStats,
    unitLabel,
    emptyMessage
}: ReportPageLayoutProps<TDto>) {
    /* Doua stari distincte, nu una:
         `esecFaraDate`  — n-avem ce arata. Continutul se INLOCUIESTE, si totalul
                           nu se mai randeaza deloc: un „TOTAL … 0" langa o cerere
                           picata e o afirmatie falsa, nu o lipsa de date.
         `esecPesteDate` — refetch picat peste un raport deja incarcat. Cifrele de
                           pe ecran au fost reale, deci NU se golesc; se spune doar
                           ca pot fi vechi.
       `report.isError` acopera si esecul interogarii proprii, si pe cel al
       interogarii de editii — a doua nu se vede in query, fiindca acolo
       interogarea principala nici nu porneste. Vezi `use-report-page`. */
    const areDate = report.data.length > 0;
    const esecFaraDate = report.isError && !areDate;
    const esecPesteDate = report.isError && areDate;
    /* A treia stare, si cea mai frecventa: interogarea NU s-a incheiat.
       Ordinea de precedenta, si de ce e exact asta:
         1. eroare FARA date  → starea de eroare (altfel n-ar exista iesire);
         2. DATE PREZENTE     → continut, cu fasie daca sunt vechi;
         3. altfel, nefinalizata → schelet;
         4. altfel            → continut, inclusiv un zero REAL.

       🔴 Punctul 2 e regula semnalata de S1, si nu e teoretica: `isPending` compus
       poate deveni adevarat DUPA ce raportul s-a incarcat (comutare de editie,
       refetch de fundal). Cu trei stari strict exclusive in ordinea
       schelet/eroare/continut, un raport functional ar disparea sub schelet la
       fiecare reimprospatare — o regresie mai vizibila decat defectul reparat,
       fiindca loveste exact utilizatorul care ARE date.
       `&& !areDate` e tot ce o inchide. */
    const neincheiata = report.isPending && !report.isError && !areDate;
    const fataCifre = neincheiata || esecFaraDate;
    const reincearca = report.retry ?? report.refetch;

    return (
        <SimpleReportLayout
            title={title}
            controls={
                <ReportControls
                    pagination={adapters.pagination}
                    isLoading={report.loading}
                    onRefresh={reincearca}
                    onResetFilters={report.resetFilters}
                    onExport={handleExport}
                    extraControls={extraControls}
                    showExport={showExport}
                    /* Exportul si resetarea filtrelor se dezactiveaza, filtrele
                       RAMAN cu valorile lor — „Reîncearcă" trebuie sa refaca exact
                       aceeasi interogare. Exportul mai ales: altfel produce un
                       fisier care afirma zero. */
                    disabled={fataCifre}
                />
            }
            pagination={
                /* Paginarea dispare odata cu tabelul: „1-0 din 0" e tot o cifra
                   care afirma ceva. */
                fataCifre ? undefined : (
                    <div className="hidden md:block">
                        <SpringPagination
                            currentPage={report.currentPage}
                            totalPages={report.totalPages}
                            totalElements={report.totalElements}
                            pageSize={report.pageSize}
                            onPageChange={report.goToPage}
                            onPageSizeChange={report.changePageSize}
                        />
                    </div>
                )
            }
            loading={report.loading}
        >
            {neincheiata ? (
                <ReportPendingState />
            ) : esecFaraDate ? (
                <ReportErrorState onRetry={reincearca} loading={report.loading} />
            ) : (
                <>
                    {esecPesteDate && (
                        <ReportStaleBar onRetry={reincearca} loading={report.loading} />
                    )}

                    <div className="hidden md:block">
                        <DataTable
                            columns={columns}
                            data={report.data}
                            onSort={report.changeSort}
                            currentSort={report.currentSort}
                            onFilter={(field, value) => report.updateFilter({ [field]: value })}
                            filterValues={report.filter as Record<string, string>}
                            emptyMessage={emptyMessage}
                        />
                    </div>

                    <div className="md:hidden">
                        <ReportMobileSummary
                            columns={columns}
                            data={report.data}
                            totalElements={report.totalElements}
                            entityLabel={entityLabel}
                            stats={mobileStats}
                            unitLabel={unitLabel}
                            loading={report.loading}
                            emptyMessage={emptyMessage}
                        />
                    </div>
                </>
            )}
        </SimpleReportLayout>
    );
}
